/**
 * Hot Topics management — the page any team uses to publish short live
 * notices to the intranet's ONE Hot Topics widget.
 *
 *   import { mountHotTopicAdmin } from '@isg/ui-kit';
 *   const admin = mountHotTopicAdmin(container, { teamLabel: 'IT' });
 *   return () => admin.destroy();
 *
 * Backend: `isg_common.hot_topics.build_hot_topic_blueprints` (default
 * `/api/hot-topics`). Ported from isg-purchasing's own page (the original,
 * which still runs its own copy because it has ERP entities and part links).
 *
 * ## This is a PUBLISHING surface, so it says who will see it
 * The single most important thing the page does is make the audience obvious
 * BEFORE the post goes out. The picker options carry a head count because most
 * people have no department recorded — "Sales" can reach four people, and a
 * limit that reaches nobody looks, from here, exactly like one that reaches
 * everybody.
 *
 * ## Options for an app with its own stored vocabulary
 * - `visibilityInternalValue` (default `'internal'`): the value the app STORES
 *   for "team only". Purchasing predates the shared model and stores
 *   `'purchasing'`; configure the page, never rewrite stored rows.
 * - `defaultVisibility` (default `'public'`): what a NEW topic starts as.
 * - `rowNote(topic)`: optional plain-text line under a row (escaped here) —
 *   purchasing's "about <part>" subject.
 * No query-param hook: an app that scopes calls (purchasing's company
 * switcher) patches the shared `api` singleton, which these calls go through.
 *
 * ⚠️ Announcements are the INTRANET's job. Hot Topics are a team's live
 * notices — an outage, a policy deadline, a supplier delay. Keep it that way.
 */
import { api } from '../services/api.js';
import { escapeHtml } from '../utils/helpers.js';
import { showError, showToast } from './toast.js';
import { showModal } from './modal.js';
import { formatDate } from '../utils/formatters.js';
import { getLocale, t } from '../i18n/index.js';

const STYLE_ID = 'isg-hot-topic-admin-styles';
const CSS = `
.hta-card .card-header{display:flex;flex-wrap:wrap;gap:var(--space-3);align-items:center;justify-content:space-between}
.hta-title{font-weight:600}
.hta-desc{font-size:var(--text-sm);color:var(--text-secondary);margin-top:2px;white-space:pre-line}
.hta-sub{font-size:var(--text-xs);color:var(--text-tertiary);margin-top:2px;word-break:break-all}
.hta-muted{color:var(--text-tertiary)}
.hta-late{color:var(--red-600)}
.hta-ok{color:var(--green-700)}
.hta-empty{text-align:center!important;color:var(--text-tertiary);padding:var(--space-6)!important;display:block}
.hta-expired td{opacity:.65}
.hta-actions{white-space:nowrap;text-align:right}
.hta-actions .btn{margin-left:var(--space-1)}
.hta-aud{display:inline-block;font-size:var(--text-xs);padding:2px 8px;border-radius:999px}
.hta-aud-internal{background:var(--gray-100);color:var(--gray-700)}
.hta-aud-all{background:var(--green-50);color:var(--green-700)}
.hta-aud-scoped{background:var(--blue-50);color:var(--blue-700)}
.hta-aud-row{display:flex;gap:var(--space-2);flex-wrap:wrap}
.hta-aud-row .form-select{flex:1;min-width:140px}
.hta-chips{display:flex;flex-wrap:wrap;gap:var(--space-2);margin-top:var(--space-2)}
.hta-chip{display:inline-flex;align-items:center;gap:4px;font-size:var(--text-xs);background:var(--blue-50);color:var(--blue-700);border-radius:999px;padding:2px 4px 2px 10px}
.hta-chip button{border:0;background:none;color:inherit;cursor:pointer;font-size:14px;line-height:1;padding:0 4px}
.hta-note{font-size:var(--text-xs);color:var(--text-secondary);margin:var(--space-2) 0 0}
.hta-warn-text{color:var(--amber-700)}
.hta-summary{font-size:var(--text-sm);margin:var(--space-2) 0 0}
.hta-reach{color:var(--text-secondary)}
.hta-warning{font-size:var(--text-xs);background:var(--amber-50);color:var(--amber-800);border-radius:var(--radius-md);padding:var(--space-2) var(--space-3);margin:0}
.hta-modal-topic{font-weight:600;margin:0 0 var(--space-2)}
.hta-modal-note{font-size:var(--text-sm);color:var(--text-secondary);margin:0}
@media (prefers-color-scheme: dark){:root:not([data-theme="light"]) .hta-aud-internal{background:var(--gray-800);color:var(--gray-200)}}
:root[data-theme="dark"] .hta-aud-internal{background:var(--gray-800);color:var(--gray-200)}
`;

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

const df = (iso) => (iso ? formatDate(iso) : null);
/* A date input's own format in the reader's LOCAL day. toISOString() is UTC,
 * and between 00:00 and 01:00 BST it names yesterday. */
const pad = (n) => String(n).padStart(2, '0');
const dateInput = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const OPTION_LISTS = { company: 'companies', country: 'countries', department: 'departments' };
/* "A, B or C" in the reader's language (Intl knows each language's own "or"). */
const orList = (xs) => {
  try {
    return new Intl.ListFormat(getLocale(), { type: 'disjunction' }).format(xs);
  } catch {
    return xs.join(', ');
  }
};
const bareConds = (conds) => (conds || []).map(({ type, value }) => ({ type, value: String(value) }));
const condsKey = (conds) => JSON.stringify(bareConds(conds).map((c) => `${c.type}\u0000${c.value}`).sort());
const people = (n) => (n === 0 ? t('hotTopics.nobody') : t('hotTopics.people_count', { count: n }));

export function mountHotTopicAdmin(container, cfg = {}) {
  ensureStyles();
  const base = (cfg.apiBase || '/hot-topics').replace(/\/$/, '');
  const team = cfg.teamLabel || t('hotTopics.default_team');
  const title = cfg.title || t('hotTopics.title');
  const intro = cfg.intro || escapeHtml(t('hotTopics.intro', { team }));
  const warning = cfg.warning || `<strong>${escapeHtml(t('hotTopics.warning_title'))}</strong>
    ${escapeHtml(t('hotTopics.warning_body'))}`;
  const titlePlaceholder = cfg.titlePlaceholder || t('hotTopics.form.title_placeholder');
  const internalVis = cfg.visibilityInternalValue || 'internal';
  const defaultVis = cfg.defaultVisibility || 'public';
  const rowNote = typeof cfg.rowNote === 'function' ? cfg.rowNote : () => '';

  let show = 'live';
  let kinds = [];
  let audience = null;       // Promise<options | {failed:true}>
  let opening = false;
  let alive = true;

  const kindLabel = (k) => (kinds.find((x) => x.value === k) || {}).label || k;

  function loadAudience() {
    if (!audience) {
      audience = api.get(`${base}/audience`).catch((err) => {
        audience = null;           // try again next time rather than caching a blip
        return { failed: true, error: err.message };
      });
    }
    return audience;
  }

  function conditionLabel(c, opts) {
    const list = (opts && !opts.failed && opts[OPTION_LISTS[c.type]]) || [];
    const hit = list.find((o) => o.value === String(c.value) || String(o.id) === String(c.value));
    return hit ? hit.label : String(c.value);
  }

  /* The combining rule is the estate's (isg_common.documents.conditions):
   * companies and countries are alternatives; a department ALSO has to match.
   * Written out as a sentence because nobody infers that from chips. */
  function describeAudience(conds, opts) {
    if (!conds.length) return t('hotTopics.audience.everyone');
    const names = (type) => conds.filter((c) => c.type === type).map((c) => conditionLabel(c, opts));
    const companies = names('company');
    const countries = names('country');
    const departments = names('department');
    /* One whole sentence per combination — never a sentence stitched from
     * fragments, which no other language can reorder. */
    const shape = [
      companies.length ? 'companies' : '',
      countries.length ? 'countries' : '',
      departments.length ? 'departments' : '',
    ].filter(Boolean).join('_') || 'none';
    return t(`hotTopics.audience.shows_${shape}`, {
      companies: orList(companies),
      countries: orList(countries),
      departments: orList(departments),
    });
  }

  function audienceLabel(topic, opts) {
    if (topic.visibility !== 'public') {
      return `<span class="hta-aud hta-aud-internal">${escapeHtml(t('hotTopics.audience.team_only', { team }))}</span>`;
    }
    const conds = topic.conditions || [];
    if (!conds.length) return `<span class="hta-aud hta-aud-all">${escapeHtml(t('hotTopics.audience.everyone_short'))}</span>`;
    return `<span class="hta-aud hta-aud-scoped" title="${escapeHtml(describeAudience(conds, opts))}">${conds
      .map((c) => escapeHtml(conditionLabel(c, opts))).join(' · ')}</span>`;
  }

  /* Repaints ONLY the audience block, so nothing typed elsewhere is lost. */
  function paintAudience(form, opts) {
    const box = form.querySelector('.hta-aud-box');
    const st = form.aud;
    const isPublic = form.querySelector('[name=visibility]').value === 'public';
    if (!isPublic) {
      box.innerHTML = st.conditions.length
        ? `<p class="hta-note">${escapeHtml(t('hotTopics.audience.internal_kept', {
          team,
          limit: st.conditions.map((c) => conditionLabel(c, opts)).join(' · '),
        }))}</p>`
        : '';
      return;
    }
    if (opts.failed) {
      box.innerHTML = `<p class="hta-note hta-warn-text">${escapeHtml(t('hotTopics.audience.lists_failed'))}${
        st.conditions.length ? ` ${escapeHtml(t('hotTopics.audience.existing_kept'))}` : ''}</p>`;
      return;
    }
    const list = opts[OPTION_LISTS[st.type]] || [];
    const taken = new Set(st.conditions.filter((c) => c.type === st.type).map((c) => String(c.value)));
    box.innerHTML = `
      <label class="form-label">${escapeHtml(t('hotTopics.audience.limit_label'))}
        <span class="hta-muted">${escapeHtml(t('hotTopics.audience.limit_hint'))}</span></label>
      <div class="hta-aud-row">
        <select class="form-select" data-cond-type aria-label="${escapeHtml(t('hotTopics.audience.limit_by'))}">
          ${opts.types.map((ty) => `<option value="${ty.value}"${ty.value === st.type ? ' selected' : ''}>${escapeHtml(ty.label)}</option>`).join('')}
        </select>
        <select class="form-select" data-cond-value aria-label="${escapeHtml(t('hotTopics.audience.value'))}">
          <option value="">${escapeHtml(t('hotTopics.audience.choose'))}</option>
          ${list.filter((o) => !taken.has(o.value)).map((o) => `<option value="${escapeHtml(o.value)}">${escapeHtml(o.label)} · ${escapeHtml(people(o.people))}</option>`).join('')}
        </select>
        <button type="button" class="btn btn-secondary btn-sm" data-cond-add>${escapeHtml(t('common.add'))}</button>
      </div>
      ${st.type === 'department' ? `
        <p class="hta-note hta-warn-text">${escapeHtml(t('hotTopics.audience.department_warning', {
          with: opts.people_with_department, count: opts.people_total }))}</p>` : ''}
      ${st.conditions.length ? `
        <div class="hta-chips">
          ${st.conditions.map((c, i) => `
            <span class="hta-chip">${escapeHtml(conditionLabel(c, opts))}
              <button type="button" data-cond-remove="${i}" aria-label="${escapeHtml(t('hotTopics.audience.remove', { name: conditionLabel(c, opts) }))}">&times;</button>
            </span>`).join('')}
        </div>` : ''}
      <p class="hta-summary">${escapeHtml(describeAudience(st.conditions, opts))}
        <span class="hta-reach" data-reach></span></p>`;

    box.querySelector('[data-cond-type]').addEventListener('change', (e) => {
      st.type = e.target.value;
      paintAudience(form, opts);
    });
    box.querySelector('[data-cond-add]').addEventListener('click', () => {
      const value = box.querySelector('[data-cond-value]').value;
      if (!value) return;
      st.conditions.push({ type: st.type, value });
      paintAudience(form, opts);
    });
    box.querySelectorAll('[data-cond-remove]').forEach((b) => {
      b.addEventListener('click', () => {
        st.conditions.splice(Number(b.dataset.condRemove), 1);
        paintAudience(form, opts);
      });
    });
    showReach(form);
  }

  /* Numbered, so a slow answer for an older set of limits never overwrites
   * the answer for the current one. */
  async function showReach(form) {
    const el = form.querySelector('[data-reach]');
    if (!el) return;
    const seq = (form.aud.reachSeq = (form.aud.reachSeq || 0) + 1);
    el.textContent = '';
    try {
      const r = await api.post(`${base}/audience/reach`, { conditions: bareConds(form.aud.conditions) });
      if (seq !== form.aud.reachSeq || !el.isConnected) return;
      el.classList.toggle('hta-warn-text', r.people === 0);
      el.textContent = r.people === 0
        ? t('hotTopics.audience.reach_nobody')
        : t('hotTopics.audience.reach', {
          people: new Intl.NumberFormat(getLocale()).format(r.people), count: r.of,
        });
    } catch (_) {
      if (seq === form.aud.reachSeq && el.isConnected) el.textContent = t('hotTopics.audience.reach_failed');
    }
  }

  function whenLabel(topic) {
    if (topic.status === 'expired') return `<span class="hta-late">${escapeHtml(t('hotTopics.when.expired', { date: df(topic.expires_at) }))}</span>`;
    if (topic.status === 'scheduled') return `<span class="hta-muted">${escapeHtml(t('hotTopics.when.from', { date: df(topic.publish_from) }))}</span>`;
    return topic.expires_at
      ? `<span class="hta-ok">${escapeHtml(t('hotTopics.when.until', { date: df(topic.expires_at) }))}</span>`
      : `<span class="hta-muted">${escapeHtml(t('hotTopics.when.no_expiry'))}</span>`;
  }

  function rowHtml(topic, opts) {
    const expired = topic.status === 'expired';
    const col = (k) => escapeHtml(t(`hotTopics.col.${k}`));
    return `
      <tr class="${expired ? 'hta-expired' : ''}">
        <td data-label="${col('topic')}" data-mobile="title">
          <div class="hta-title">${topic.is_pinned ? '📌 ' : ''}${escapeHtml(topic.title)}</div>
          ${topic.body ? `<div class="hta-desc">${escapeHtml(topic.body)}</div>` : ''}
          ${rowNote(topic) ? `<div class="hta-sub">${escapeHtml(rowNote(topic))}</div>` : ''}
          ${topic.link_url ? `<div class="hta-sub">${escapeHtml(topic.link_url)}</div>` : ''}
        </td>
        <td data-label="${col('kind')}"><span class="badge badge-gray">${escapeHtml(kindLabel(topic.kind))}</span></td>
        <td data-label="${col('who')}">${audienceLabel(topic, opts)}</td>
        <td data-label="${col('until')}">${whenLabel(topic)}</td>
        <td class="hta-actions" data-mobile="bare">
          <button class="btn btn-secondary btn-sm" data-act="edit" data-id="${topic.id}">${escapeHtml(t('common.edit'))}</button>
          ${expired
            ? `<button class="btn btn-primary btn-sm" data-act="republish" data-id="${topic.id}">${escapeHtml(t('hotTopics.republish'))}</button>`
            : `<button class="btn btn-secondary btn-sm" data-act="expire" data-id="${topic.id}">${escapeHtml(t('hotTopics.expire'))}</button>`}
          <button class="btn btn-ghost btn-sm" data-act="delete" data-id="${topic.id}">${escapeHtml(t('common.delete'))}</button>
        </td>
      </tr>`;
  }

  /* Publish and Edit: ONE form — two copies is how the edit screen quietly
   * stops offering a field the publish screen gained. */
  function topicForm(topic, opts) {
    const v = topic || {};
    const exp = v.expires_at ? dateInput(new Date(v.expires_at)) : '';
    const opt = (value, label, current) => `<option value="${escapeHtml(value)}"${value === current ? ' selected' : ''}>${escapeHtml(label)}</option>`;
    const vis = v.visibility || defaultVis;
    const form = document.createElement('div');
    form.className = 'form-stack';
    form.innerHTML = `
      <div class="form-group">
        <label class="form-label">${escapeHtml(t('hotTopics.form.title'))}</label>
        <input name="title" class="form-input" required maxlength="300"
               value="${escapeHtml(v.title || '')}" placeholder="${escapeHtml(titlePlaceholder)}">
      </div>
      <div class="form-group">
        <label class="form-label">${escapeHtml(t('hotTopics.form.detail'))}</label>
        <textarea name="body" class="form-textarea" rows="3"
                  placeholder="${escapeHtml(t('hotTopics.form.detail_placeholder'))}">${escapeHtml(v.body || '')}</textarea>
      </div>
      <div class="form-group">
        <label class="form-label">${escapeHtml(t('hotTopics.form.link'))} <span class="hta-muted">${escapeHtml(t('hotTopics.form.link_hint'))}</span></label>
        <input name="link_url" type="url" class="form-input" value="${escapeHtml(v.link_url || '')}"
               placeholder="https://…">
      </div>
      <div class="form-group">
        <label class="form-label">${escapeHtml(t('hotTopics.col.kind'))}</label>
        <select name="kind" class="form-select">
          ${kinds.map((k) => opt(k.value, k.label, v.kind || (kinds[0] && kinds[0].value))).join('')}
        </select>
      </div>
      <div class="form-group">
        <label class="form-label">${escapeHtml(t('hotTopics.form.who'))}</label>
        <select name="visibility" class="form-select">
          ${opt('public', t('hotTopics.form.vis_public'), vis)}
          ${opt(internalVis, t('hotTopics.form.vis_internal', { team }), vis)}
        </select>
      </div>
      <div class="form-group hta-aud-box"></div>
      <div class="form-group">
        <label class="form-label">${escapeHtml(t('hotTopics.col.until'))} <span class="hta-muted">${escapeHtml(t('hotTopics.form.until_hint'))}</span></label>
        <input name="expires_at" type="date" class="form-input" value="${exp}" data-initial="${exp}">
      </div>
      <div class="form-group">
        <label class="form-label"><input type="checkbox" name="is_pinned"${v.is_pinned ? ' checked' : ''}> ${escapeHtml(t('hotTopics.form.pin'))}</label>
      </div>
      <p class="hta-warning">${warning}</p>`;
    form.aud = { conditions: bareConds(v.conditions), initial: condsKey(v.conditions), type: 'company' };
    form.querySelector('[name=visibility]').addEventListener('change', () => paintAudience(form, opts));
    paintAudience(form, opts);
    return form;
  }

  /* ⚠️ On EDIT the expiry and audience are sent only when CHANGED: a bare date
   * re-sent is read as end-of-day and would put an expired topic back out. */
  function readTopicForm(form, topic) {
    const q = (n) => form.querySelector(`[name=${n}]`);
    const payload = {
      title: q('title').value.trim(),
      body: q('body').value.trim() || null,
      link_url: q('link_url').value.trim() || null,
      kind: q('kind').value,
      visibility: q('visibility').value,
      is_pinned: q('is_pinned').checked,
    };
    const exp = q('expires_at');
    if (!topic || exp.value !== exp.dataset.initial) payload.expires_at = exp.value || null;
    if (!topic || condsKey(form.aud.conditions) !== form.aud.initial) {
      payload.conditions = bareConds(form.aud.conditions);
    }
    return payload;
  }

  async function openTopicModal(topic) {
    if (opening) return;          // a double click would open two focus traps
    opening = true;
    let opts;
    try { opts = await loadAudience(); } finally { opening = false; }
    const editing = Boolean(topic);
    const form = topicForm(topic, opts);
    showModal({
      title: escapeHtml(editing ? t('hotTopics.modal.edit_title') : t('hotTopics.modal.publish_title')),
      content: form,
      size: 'md',
      actions: [
        { label: t('common.cancel'), className: 'btn btn-secondary' },
        {
          label: editing ? t('hotTopics.save_changes') : t('hotTopics.publish'),
          className: 'btn btn-primary',
          onClick: async (close) => {
            const payload = readTopicForm(form, topic);
            if (!payload.title) { showError(t('hotTopics.toast.needs_title')); return; }
            try {
              if (editing) await api.patch(`${base}/${topic.id}`, payload);
              else await api.post(base, payload);
              showToast(editing ? t('hotTopics.toast.saved') : t('hotTopics.toast.published'), 'success');
              close();
              load();
            } catch (err) {
              showError(err.message || (editing ? t('hotTopics.toast.save_failed') : t('hotTopics.toast.publish_failed')));
            }
          },
        },
      ],
    });
  }

  function openRepublish(topic, opts) {
    const form = document.createElement('div');
    form.className = 'form-stack';
    form.innerHTML = `
      <div>
        <p class="hta-modal-topic">${escapeHtml(topic.title)}</p>
        <p class="hta-modal-note">${t('hotTopics.modal.goes_back_to', { audience: audienceLabel(topic, opts) })}</p>
      </div>
      <div class="form-group">
        <label class="form-label">${escapeHtml(t('hotTopics.col.until'))} <span class="hta-muted">${escapeHtml(t('hotTopics.form.until_hint'))}</span></label>
        <input name="until" type="date" class="form-input" min="${dateInput()}">
      </div>`;
    showModal({
      title: escapeHtml(t('hotTopics.modal.republish_title')),
      content: form,
      size: 'md',
      actions: [
        { label: t('common.cancel'), className: 'btn btn-secondary' },
        {
          label: t('hotTopics.republish'),
          className: 'btn btn-primary',
          onClick: async (close) => {
            const until = form.querySelector('[name=until]').value;
            if (until && until < dateInput()) { showError(t('hotTopics.toast.date_passed')); return; }
            try {
              await api.post(`${base}/${topic.id}/republish`, { expires_at: until || null });
              showToast(t('hotTopics.toast.republished'), 'success');
              close();
              load();
            } catch (err) { showError(err.message || t('hotTopics.toast.republish_failed')); }
          },
        },
      ],
    });
  }

  function openDelete(topic) {
    const alternative = topic.status === 'expired'
      ? t('hotTopics.modal.delete_expired')
      : t('hotTopics.modal.delete_live');
    showModal({
      title: escapeHtml(t('hotTopics.modal.delete_title')),
      content: `
        <p class="hta-modal-topic">${escapeHtml(topic.title)}</p>
        <p class="hta-modal-note">${escapeHtml(t('hotTopics.modal.delete_body'))} ${escapeHtml(alternative)}</p>`,
      size: 'md',
      actions: [
        { label: t('common.cancel'), className: 'btn btn-secondary' },
        {
          label: t('common.delete'),
          className: 'btn btn-danger',
          onClick: async (close) => {
            try {
              await api.delete(`${base}/${topic.id}`);
              showToast(t('hotTopics.toast.deleted'), 'success');
              close();
              load();
            } catch (err) { showError(err.message || t('hotTopics.toast.delete_failed')); }
          },
        },
      ],
    });
  }

  async function load() {
    const body = container.querySelector('[data-hta-body]');
    if (!body) return;
    body.innerHTML = `<tr><td colspan="5" class="hta-empty" data-mobile="bare">${escapeHtml(t('hotTopics.loading'))}</td></tr>`;
    try {
      const [d, opts] = await Promise.all([
        api.get(`${base}${show === 'live' ? '' : '?all=1'}`),
        loadAudience(),
      ]);
      if (!alive) return;
      kinds = d.kinds || [];
      const topics = show === 'expired' ? d.topics.filter((topic) => topic.status === 'expired') : d.topics;
      const empty = show === 'expired'
        ? t('hotTopics.empty.expired')
        : show === 'all' ? t('hotTopics.empty.all') : t('hotTopics.empty.live');
      body.innerHTML = topics.length
        ? topics.map((topic) => rowHtml(topic, opts)).join('')
        : `<tr><td colspan="5" class="hta-empty" data-mobile="bare">${escapeHtml(empty)}</td></tr>`;
      const byId = new Map(topics.map((topic) => [String(topic.id), topic]));
      body.querySelectorAll('[data-act]').forEach((b) => {
        b.addEventListener('click', async () => {
          const topic = byId.get(b.dataset.id);
          if (b.dataset.act === 'edit') openTopicModal(topic);
          else if (b.dataset.act === 'republish') openRepublish(topic, opts);
          else if (b.dataset.act === 'delete') openDelete(topic);
          else if (b.dataset.act === 'expire') {
            try {
              await api.patch(`${base}/${topic.id}`, { expires_at: new Date().toISOString() });
              showToast(t('hotTopics.toast.expired'), 'success');
              load();
            } catch (err) { showError(err.message || t('hotTopics.toast.expire_failed')); }
          }
        });
      });
    } catch (err) {
      if (!alive) return;
      body.innerHTML = `<tr><td colspan="5" class="hta-empty" data-mobile="bare">${escapeHtml(err.message || t('hotTopics.load_failed'))}</td></tr>`;
    }
  }

  container.innerHTML = `
    <div class="main-content-inner">
      <div class="page-banner"><div class="page-banner-inner"><div>
        <h1>${escapeHtml(title)}</h1>
        <p>${intro}</p>
      </div></div></div>
      <div class="card hta-card">
        <div class="card-header">
          <div class="filter-pills" role="tablist">
            <button class="filter-pill active" data-show="live">${escapeHtml(t('hotTopics.show.live'))}</button>
            <button class="filter-pill" data-show="expired">${escapeHtml(t('hotTopics.show.expired'))}</button>
            <button class="filter-pill" data-show="all">${escapeHtml(t('hotTopics.show.all'))}</button>
          </div>
          <button class="btn btn-primary" data-hta-new>${escapeHtml(t('hotTopics.publish_topic'))}</button>
        </div>
        <div class="table-responsive">
          <table class="data-table table-cards">
            <thead><tr>
              <th>${escapeHtml(t('hotTopics.col.topic'))}</th><th>${escapeHtml(t('hotTopics.col.kind'))}</th><th>${escapeHtml(t('hotTopics.col.who'))}</th><th>${escapeHtml(t('hotTopics.col.until'))}</th><th></th>
            </tr></thead>
            <tbody data-hta-body></tbody>
          </table>
        </div>
      </div>
    </div>`;

  container.querySelectorAll('[data-show]').forEach((b) => {
    b.addEventListener('click', () => {
      container.querySelectorAll('[data-show]').forEach((x) => x.classList.remove('active'));
      b.classList.add('active');
      show = b.dataset.show;
      load();
    });
  });
  container.querySelector('[data-hta-new]').addEventListener('click', async () => {
    if (!kinds.length) await load();       // the kind list rides on the list call
    openTopicModal(null);
  });
  load();

  return { destroy() { alive = false; }, reload: load };
}
